import { useEffect, useMemo, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import useUser from '../authentication/context/useUser';

import Tooltip from '@mui/material/Tooltip';
import { ReactComponent as Export } from '../ui/icons/export.svg';
import ShareProjectModal from './ShareProjectModal';

import { Project, ProjectUser } from '@arra/core';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SyncIcon from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';
import DownloadButton from '../layout/Header/DownloadButton';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { HeaderButton, IconContainer } from '../layout/Header';
import { isPage } from '../layout/Header/ProjectBreadCrumb';
import Button from '../ui/Button';
import Markdown from '../ui/Markdown';
import keyify from './parameters/keyify';
import { useProject } from './context/useProject';
import { getServiceTypeFromProject } from '../analytics/posthogEvents';


const ProjectActions = ({ project }: { project: Project }) => {
  const { projectId } = useParams();
  const { isUpdatingParameters } = useProject(projectId);
  const location = useLocation();
  const { isProject } = isPage(location);

  // hide labels under 1400px
  const isCompact = useMediaQuery('(max-width:1399px)');

  if (!isProject) return null;

  return (
    <>
      <InfoButton project={project} isCompact={isCompact} />
      <ShareProject project={project} isCompact={isCompact} />
      <DownloadButton project={project} isCompact={isCompact} withIcon={true} />
      <SaveStatus loading={isUpdatingParameters} />
    </>
  );
};

const InfoButton = ({
  project,
  isCompact,
}: {
  project: Project;
  isCompact: boolean;
}) => {
  const { t } = useTranslation(['project']);
  const [open, setOpen] = useState(false);
  const posthog = usePostHog();

  const handleOpen = () => {
    posthog?.capture('project_info clicked', {
      button_name: 'project_info',
      page_name: 'project',
      model_type: getServiceTypeFromProject(project),
    });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const modelIntroText = t(`model:${keyify(project.model.name)}.introText`);

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('project:info-button.modalTitle')}</DialogTitle>
        <DialogContent dividers>
          <Markdown>{modelIntroText}</Markdown>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            {t('common:ok')}
          </Button>
        </DialogActions>
      </Dialog>

      <Tooltip title={t('project:info-button.tooltip')}>
        <HeaderButton onClick={handleOpen}>
          <IconContainer>
            <InfoOutlinedIcon style={{ fill: 'white' }} />
          </IconContainer>
          {!isCompact && <p className="h6r">{t('project:info-button.label')}</p>}
        </HeaderButton>
      </Tooltip>
    </>
  );
};

const ShareProject = ({
  project,
  isCompact,
}: {
  project: Project;
  isCompact: boolean;
}) => {
  const { user } = useUser();
  const [openShare, setOpenShare] = useState<boolean>(false);
  const { t } = useTranslation(['project']);
  const posthog = usePostHog();

  const handleShareProject = () => {
    posthog?.capture('project_share clicked', {
      button_name: 'project_share',
      page_name: 'project',
      model_type: getServiceTypeFromProject(project),
    });
    setOpenShare(true);
  };

  const isShareProject = project?.users?.some(
    (u: ProjectUser) => u.id === user?._id && u.role === 'owner',
  );

  return (
    <>
      <ShareProjectModal
        open={openShare}
        onClose={() => setOpenShare(false)}
        project={project}
      />

      {isShareProject && (
        <Tooltip title={t('project:share-button.tooltip')}>
          <HeaderButton onClick={handleShareProject}>
            <IconContainer>
              <Export style={{ fill: 'white' }} />
            </IconContainer>
            {!isCompact && (
              <p className="h6r">{t('project:share-button.label')}</p>
            )}
          </HeaderButton>
        </Tooltip>
      )}
    </>
  );
};

const SaveStatus = ({ loading }: { loading: boolean }) => {
  const [justSaved, setJustSaved] = useState(false);
  const prevLoading = useRef(loading);

  const { t } = useTranslation(['project']);

  useEffect(() => {
    let timeoutId: number | undefined;

    if (prevLoading.current && !loading) {
      setJustSaved(true);
      timeoutId = window.setTimeout(() => setJustSaved(false), 2000);
    }

    prevLoading.current = loading;

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [loading]);

  const content = useMemo(() => {
    if (loading) {
      return { icon: <SyncIcon style={{ fill: 'white' }}  />, label: t('project:save-icon.saving') };
    }
    if (justSaved) {
      return { icon: <CloudDoneIcon style={{ fill: 'white' }} />, label: t('project:save-icon.saved') };
    }
    return { icon: <CloudDoneIcon style={{ fill: 'white' }} />, label: null as string | null };
  }, [loading, justSaved]);

  return (
    <HeaderButton
    >
      <IconContainer>{content.icon}</IconContainer>
      {content.label && <p className="h6r">{content.label}</p>}
    </HeaderButton>
  );
};


export default ProjectActions;
